import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Select,
  MenuItem,
  List,
  ListItem,
  IconButton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocationCity as LocationCityIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { City } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// NOTE: General, Subscriptions, Verification, and Analytics tabs were removed from
// this page (not just hidden) — they had no backend behind them at all (see the
// Settings page audit). Only tabs/fields with a real, working backend are shown here.
// Re-add a tab once its backend settings + consumption points actually exist.
const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [defaultCity, setDefaultCity] = useState('');
  const [cities, setCities] = useState<string[]>([]);

  // Load current default city on component mount
  useEffect(() => {
    loadDefaultCity();
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      const response = await apiService.getCities();
      const cityNames = (response.data as City[])
        .filter((city) => city.is_active)
        .map((city) => city.name);
      setCities(cityNames);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadDefaultCity = async () => {
    try {
      setSaving(true);
      const response = await apiService.getDefaultCity();
      setDefaultCity(response || '');
      setSaveMessage(null);
    } catch (error: any) {
      console.error('Error loading default city:', error);
      setSaveMessage({ type: 'error', text: 'Failed to load default city' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDefaultCity = async () => {
    if (!defaultCity.trim()) {
      setSaveMessage({ type: 'error', text: 'Please enter a city name' });
      return;
    }

    try {
      setSaving(true);
      const response = await apiService.setDefaultCity(defaultCity);
      if (response.success) {
        setSaveMessage({ type: 'success', text: 'Default city updated successfully!' });
      } else {
        setSaveMessage({ type: 'error', text: response.message || 'Failed to update default city' });
      }
    } catch (error: any) {
      console.error('Error saving default city:', error);
      setSaveMessage({ type: 'error', text: 'Failed to update default city' });
    } finally {
      setSaving(false);
    }
  };

  // Lead Management Settings
  const [fastResponderThreshold, setFastResponderThreshold] = useState(2); // hours
  const [defaultAutoClosure, setDefaultAutoClosure] = useState(14); // days

  // Notification Settings
  const [pushNotifications, setPushNotifications] = useState(true);

  // Security Settings
  const [minPasswordLength, setMinPasswordLength] = useState(8);

  // Payment & Commission Settings
  const [defaultCommissionPercentage, setDefaultCommissionPercentage] = useState(10);
  const [defaultPlatformFee, setDefaultPlatformFee] = useState(0);
  const [categoryCommissions, setCategoryCommissions] = useState<Array<{
    category_id: number;
    category_name: string;
    commission_percentage: number;
    platform_fee: number;
  }>>([]);
  const [availableCategories, setAvailableCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedCategoryForAdd, setSelectedCategoryForAdd] = useState<number | ''>('');

  // Load payment settings on mount
  useEffect(() => {
    const loadPaymentSettings = async () => {
      try {
        const settings = await apiService.getPaymentSettings();
        setDefaultCommissionPercentage(settings.default.commission_percentage);
        setDefaultPlatformFee(settings.default.platform_fee);
        setCategoryCommissions(settings.category_commissions || []);
        setAvailableCategories(settings.categories || []);
      } catch (error) {
        console.error('Error loading payment settings:', error);
      }
    };
    loadPaymentSettings();
  }, []);

  // Load lead management settings on mount
  useEffect(() => {
    const loadLeadManagementSettings = async () => {
      try {
        const settings = await apiService.getLeadManagementSettings();
        setFastResponderThreshold(settings.fast_responder_threshold);
        setDefaultAutoClosure(settings.default_lead_auto_closure_days);
      } catch (error) {
        console.error('Error loading lead management settings:', error);
      }
    };
    loadLeadManagementSettings();
  }, []);

  // Load notification settings on mount
  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        const settings = await apiService.getNotificationSettings();
        setPushNotifications(settings.push_notifications_enabled);
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    };
    loadNotificationSettings();
  }, []);

  // Load security settings on mount
  useEffect(() => {
    const loadSecuritySettings = async () => {
      try {
        const settings = await apiService.getSecuritySettings();
        setMinPasswordLength(settings.min_password_length);
      } catch (error) {
        console.error('Error loading security settings:', error);
      }
    };
    loadSecuritySettings();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);

    try {
      if (tabValue === 1) {
        // Lead Management tab
        const updated = await apiService.updateLeadManagementSettings({
          fast_responder_threshold: fastResponderThreshold,
          default_lead_auto_closure_days: defaultAutoClosure,
        });
        setFastResponderThreshold(updated.fast_responder_threshold);
        setDefaultAutoClosure(updated.default_lead_auto_closure_days);
        setSaveMessage({ type: 'success', text: 'Lead management settings saved successfully!' });
      } else if (tabValue === 2) {
        // Notifications tab
        const updated = await apiService.updateNotificationSettings({
          push_notifications_enabled: pushNotifications,
        });
        setPushNotifications(updated.push_notifications_enabled);
        setSaveMessage({ type: 'success', text: 'Notification settings saved successfully!' });
      } else if (tabValue === 3) {
        // Security tab
        const updated = await apiService.updateSecuritySettings({
          min_password_length: minPasswordLength,
        });
        setMinPasswordLength(updated.min_password_length);
        setSaveMessage({ type: 'success', text: 'Security settings saved successfully!' });
      } else if (tabValue === 4) {
        // Payment & Commission tab
        await apiService.updatePaymentSettings({
          default: {
            commission_percentage: defaultCommissionPercentage,
            platform_fee: defaultPlatformFee,
          },
          category_commissions: categoryCommissions.map(cc => ({
            category_id: cc.category_id,
            commission_percentage: cc.commission_percentage,
            platform_fee: cc.platform_fee,
          })),
        });
        setSaveMessage({ type: 'success', text: 'Payment settings saved successfully!' });
      }
    } catch (error: any) {
      setSaveMessage({ type: 'error', text: error.response?.data?.message || error.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 2, fontSize: 32 }} color="primary" />
        <Typography variant="h4">System Settings</Typography>
      </Box>

      {saveMessage && (
        <Alert severity={saveMessage.type} sx={{ mb: 2 }} onClose={() => setSaveMessage(null)}>
          {saveMessage.text}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab icon={<LocationCityIcon />} iconPosition="start" label="Default City" />
            <Tab icon={<SettingsIcon />} iconPosition="start" label="Lead Management" />
            <Tab icon={<NotificationsIcon />} iconPosition="start" label="Notifications" />
            <Tab icon={<SecurityIcon />} iconPosition="start" label="Security" />
            <Tab icon={<PaymentIcon />} iconPosition="start" label="Payment & Commission" />
          </Tabs>
        </Box>

        {/* Default City Settings */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Default City Setting
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set the default city that will be automatically selected when users visit the homepage.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {cities.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No active cities are available. Please activate at least one city in City Management.
                </Alert>
              )}
              <Select
                fullWidth
                label="Default City"
                value={defaultCity}
                onChange={(e) => setDefaultCity(e.target.value)}
                disabled={saving || cities.length === 0}
                displayEmpty
              >
                <MenuItem value="">Select a city</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                variant="contained"
                onClick={handleSaveDefaultCity}
                disabled={saving || !defaultCity}
                startIcon={<SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Default City'}
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Lead Management Settings */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Fast Responder Threshold (hours)"
                type="number"
                value={fastResponderThreshold}
                onChange={(e) => setFastResponderThreshold(parseInt(e.target.value) || 0)}
                helperText="Response time to qualify as Fast Responder in lead statistics"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Default Auto-Closure Period (days)"
                type="number"
                value={defaultAutoClosure}
                onChange={(e) => setDefaultAutoClosure(parseInt(e.target.value) || 0)}
                helperText="Fallback used only when a service category has no override"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={pushNotifications}
                    onChange={(e) => setPushNotifications(e.target.checked)}
                  />
                }
                label="Enable Push Notifications"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5 }}>
                Master switch for all booking push notifications
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Minimum Password Length"
                type="number"
                value={minPasswordLength}
                onChange={(e) => setMinPasswordLength(parseInt(e.target.value) || 0)}
                helperText="Enforced at registration, password reset, and change password"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Payment & Commission Settings */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Commission Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure the platform commission percentage and fixed fee. You can set default rates for all categories, and override them for specific service categories.
              </Typography>
            </Grid>

            {/* Default Commission Settings */}
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Default Commission (Applies to all categories unless overridden)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Commission Percentage (%)"
                      type="number"
                      value={defaultCommissionPercentage}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 100) {
                          setDefaultCommissionPercentage(value);
                        }
                      }}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      helperText="Default percentage for all categories"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Platform Fee (INR)"
                      type="number"
                      value={defaultPlatformFee}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0) {
                          setDefaultPlatformFee(value);
                        }
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Default fee for all categories"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Default Commission Calculation Example:
                  </Typography>
                  <Typography variant="body2">
                    For a transaction of ₹1,000:
                    <br />
                    • Commission ({defaultCommissionPercentage}%): ₹{(1000 * defaultCommissionPercentage / 100).toFixed(2)}
                    <br />
                    • Platform Fee: ₹{defaultPlatformFee.toFixed(2)}
                    <br />
                    • <strong>Professional Receives: ₹{(1000 - (1000 * defaultCommissionPercentage / 100) - defaultPlatformFee).toFixed(2)}</strong>
                  </Typography>
                </Box>
              </Card>
            </Grid>

            {/* Category-Specific Commission Settings */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Category-Specific Commission (Optional)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Select
                    value={selectedCategoryForAdd}
                    onChange={(e) => setSelectedCategoryForAdd(e.target.value as number | '')}
                    displayEmpty
                    size="small"
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    {availableCategories.length === 0 ? (
                      <MenuItem value="" disabled>
                        No categories available (Total: {availableCategories.length})
                      </MenuItem>
                    ) : (
                      availableCategories
                        .filter(cat => !categoryCommissions.some(cc => cc.category_id === cat.id))
                        .map(cat => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </MenuItem>
                        ))
                    )}
                  </Select>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      if (selectedCategoryForAdd) {
                        const category = availableCategories.find(c => c.id === selectedCategoryForAdd);
                        if (category) {
                          setCategoryCommissions([
                            ...categoryCommissions,
                            {
                              category_id: category.id,
                              category_name: category.name,
                              commission_percentage: defaultCommissionPercentage,
                              platform_fee: defaultPlatformFee,
                            },
                          ]);
                          setSelectedCategoryForAdd('');
                        }
                      }
                    }}
                    disabled={!selectedCategoryForAdd}
                  >
                    Add Category
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Override default commission rates for specific service categories. Categories without overrides will use the default rates.
              </Typography>

              {categoryCommissions.length === 0 ? (
                <Alert severity="info">
                  No category-specific commissions configured. All categories will use the default rates.
                </Alert>
              ) : (
                <List>
                  {categoryCommissions.map((cc, index) => (
                    <React.Fragment key={cc.category_id}>
                      <ListItem>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {cc.category_name}
                            </Typography>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setCategoryCommissions(categoryCommissions.filter((_, i) => i !== index));
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                fullWidth
                                label="Commission Percentage (%)"
                                type="number"
                                value={cc.commission_percentage}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  if (!isNaN(value) && value >= 0 && value <= 100) {
                                    const updated = [...categoryCommissions];
                                    updated[index].commission_percentage = value;
                                    setCategoryCommissions(updated);
                                  }
                                }}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                                size="small"
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                fullWidth
                                label="Platform Fee (INR)"
                                type="number"
                                value={cc.platform_fee}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  if (!isNaN(value) && value >= 0) {
                                    const updated = [...categoryCommissions];
                                    updated[index].platform_fee = value;
                                    setCategoryCommissions(updated);
                                  }
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </ListItem>
                      {index < categoryCommissions.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Default City tab has its own dedicated "Save Default City" button above, so this generic
            Save is hidden there to avoid a second button that does nothing on that tab. */}
        {tabValue !== 0 && (
          <>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

export default SettingsPage;
